/**
 * AppliedFilters — what the catalogue is currently filtered to, as a removable
 * row (new, 2026-08).
 *
 * The shipped browse page had no applied-filter state at all. A user who tapped
 * a category on a phone got the sheet dismissed, a shorter grid, and no visible
 * account of WHY it was shorter — the only record of the filter was a `?category=`
 * in a URL they cannot see, and the sidebar that would have shown the highlight
 * was collapsed behind a button. For an audience whose controlling emotion is
 * "am I being shown everything, or being steered?", an invisible filter is a
 * trust problem, not just an affordance gap.
 *
 * So: every active facet gets a chip, each chip removes exactly its own facet,
 * and one "clear all" resets the lot. The row is drawn on the header rule so it
 * reads as part of the slip's armature rather than as floating pills.
 *
 * Two deliberate refusals. A market whose id has no matching option is NOT
 * rendered — showing a raw UUID to a buyer is worse than showing nothing (the
 * markets list is static and currently empty; STORY-003-008 may promote it to a
 * real endpoint). And the chips carry no colour: they are a statement of state,
 * not an alert, and terracotta is reserved for change and attention.
 *
 * Tokens via the `--g-*` mirror; strings from the `marketplace` namespace.
 */
import { type ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { X } from "lucide-react";

import { iconProps } from "@/lib/icons";
import { kgsLocaleForLanguage } from "@/lib/format-currency";
import { formatPriceFigure } from "@/features/marketplace/price-format";
import type { MarketOption } from "@/features/marketplace/MarketFilterMultiselect";
import type { PriceRangeValue } from "@/features/marketplace/PriceRangeInput";
import type { SortValue } from "@/features/marketplace/SortSelect";
import {
  categoryDisplayName,
  findNodeInTree,
  useCategoryTree,
} from "@/features/marketplace/useCategoryTree";

export interface AppliedFiltersProps {
  categoryId: string | null;
  onClearCategory: () => void;

  marketOptions: readonly MarketOption[];
  marketIds: readonly string[];
  onMarketChange: (ids: string[]) => void;

  inStockOnly: boolean;
  onInStockChange: (checked: boolean) => void;

  priceRange: PriceRangeValue;
  onPriceChange: (value: PriceRangeValue) => void;

  sort: SortValue;
  onSortChange: (value: SortValue) => void;

  /** Resets every facet in one action. */
  onClearAll: () => void;
}

interface Chip {
  key: string;
  label: string;
  onRemove: () => void;
}

/** One removable facet. The whole chip is the control — no nested buttons. */
function FilterChip({ chip }: { chip: Chip }): ReactElement {
  const { t } = useTranslation("marketplace");
  return (
    <button
      type="button"
      onClick={chip.onRemove}
      data-testid={`applied-filter-${chip.key}`}
      aria-label={t("filters.removeFilter", { name: chip.label })}
      className="inline-flex min-h-[44px] max-w-full items-center gap-2 rounded-[color:var(--g-radius-pill)] rounded-full border border-[color:var(--g-rule)] bg-[color:var(--g-sunken)] px-3 text-xs font-medium text-foreground transition-colors hover:border-[color:var(--g-rule-strong)] hover:bg-[color:var(--g-accent-soft)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
    >
      <span className="truncate">{chip.label}</span>
      <X {...iconProps("xs")} className="shrink-0 text-[color:var(--g-ink-2)]" />
    </button>
  );
}

export function AppliedFilters({
  categoryId,
  onClearCategory,
  marketOptions,
  marketIds,
  onMarketChange,
  inStockOnly,
  onInStockChange,
  priceRange,
  onPriceChange,
  sort,
  onSortChange,
  onClearAll,
}: AppliedFiltersProps): ReactElement | null {
  const { t, i18n } = useTranslation("marketplace");
  const locale = kgsLocaleForLanguage(i18n.language);
  // The tree is already in the query cache (the sidebar mounted it); this is a
  // cache read, not a second request.
  const { tree } = useCategoryTree();

  const chips: Chip[] = [];

  if (categoryId !== null) {
    const node = findNodeInTree(tree, categoryId);
    chips.push({
      key: "category",
      label:
        node !== null
          ? categoryDisplayName(node, i18n.language)
          : t("category.treeLabel"),
      onRemove: onClearCategory,
    });
  }

  for (const id of marketIds) {
    const option = marketOptions.find((m) => m.id === id);
    // Never surface a raw id to a buyer — drop the chip instead.
    if (option === undefined) continue;
    chips.push({
      key: `market-${id}`,
      label: option.name,
      onRemove: () => onMarketChange(marketIds.filter((m) => m !== id)),
    });
  }

  if (inStockOnly) {
    chips.push({
      key: "in-stock",
      label: t("filters.inStockOnly"),
      onRemove: () => onInStockChange(false),
    });
  }

  if (priceRange.min !== null) {
    chips.push({
      key: "price-min",
      label: t("filters.chipPriceMin", {
        value: formatPriceFigure(priceRange.min, locale),
      }),
      onRemove: () => onPriceChange({ min: null, max: priceRange.max }),
    });
  }

  if (priceRange.max !== null) {
    chips.push({
      key: "price-max",
      label: t("filters.chipPriceMax", {
        value: formatPriceFigure(priceRange.max, locale),
      }),
      onRemove: () => onPriceChange({ min: priceRange.min, max: null }),
    });
  }

  if (sort !== "relevance") {
    chips.push({
      key: "sort",
      label: t(`sort.${sort}`),
      onRemove: () => onSortChange("relevance"),
    });
  }

  if (chips.length === 0) return null;

  return (
    <div
      data-testid="applied-filters"
      aria-label={t("filters.appliedLabel")}
      role="group"
      className="flex flex-wrap items-center gap-2 border-t border-[color:var(--g-rule)] pt-3"
    >
      {chips.map((chip) => (
        <FilterChip key={chip.key} chip={chip} />
      ))}
      <button
        type="button"
        onClick={onClearAll}
        data-testid="applied-filters-clear"
        className="ml-auto inline-flex min-h-[44px] items-center px-1 text-xs font-semibold text-[color:var(--g-primary)] underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
      >
        {t("filters.clearAll")}
      </button>
    </div>
  );
}
